import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Badge, Button, Spinner, Nav, Tab, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Layout from '../components/layouts/Main';
import { useNotifications } from '../context/NotificationContext';
 
const Notifications = () => {
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    getNotificationsByType
  } = useNotifications();

  const [activeTab, setActiveTab] = useState('all');
  const [filteredNotifications, setFilteredNotifications] = useState([]);

  const filterNotifications = useCallback((filter) => {
    let filtered = [...notifications];

    switch (filter) {
      case 'unread':
        filtered = notifications.filter(n => !n.read);
        break;
      case 'products':
        filtered = getNotificationsByType('warning').filter(n =>
          n.title.toLowerCase().includes('product') ||
          n.message.toLowerCase().includes('product')
        );
        break;
      case 'packing-materials':
        filtered = getNotificationsByType('warning').filter(n =>
          n.title.toLowerCase().includes('packing material') ||
          n.message.toLowerCase().includes('packing material')
        );
        break;
      case 'overdue':
        filtered = getNotificationsByType('danger').filter(n =>
          n.title.toLowerCase().includes('overdue')
        );
        break;
      case 'all':
      default:
        // Already have all notifications
        break;
    }

    // Sort by timestamp (newest first)
    filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    setFilteredNotifications(filtered);
  }, [notifications, getNotificationsByType]);

  useEffect(() => {
    filterNotifications(activeTab);
  }, [activeTab, filterNotifications]);

  const handleMarkAsRead = (notificationId) => {
    markAsRead(notificationId);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'danger':
        return 'fas fa-exclamation-triangle text-danger';
      case 'warning':
        return 'fas fa-exclamation-circle text-warning';
      case 'info':
        return 'fas fa-info-circle text-info';
      default:
        return 'fas fa-bell text-secondary';
    }
  };

  const getNotificationBadge = (type) => {
    switch (type) {
      case 'danger':
        return <Badge bg="danger">Critical</Badge>;
      case 'warning':
        return <Badge bg="warning">Warning</Badge>;
      case 'info':
        return <Badge bg="info">Info</Badge>;
      default:
        return <Badge bg="secondary">General</Badge>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <Container fluid className="py-4">
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading notifications...</p>
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container fluid className="py-4">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active">Notifications</li>
          </ol>
        </nav>

        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-0">Notifications</h2>
                <p className="text-muted mb-0">
                  Stay updated with important alerts and notifications
                </p>
              </div>
              <div className="d-flex gap-2">
                {unreadCount > 0 && (
                  <Button
                    variant="outline-primary"
                    onClick={handleMarkAllAsRead}
                  >
                    <i className="fas fa-check-double me-2"></i>
                    Mark All as Read
                  </Button>
                )}
                <Button
                  variant="outline-secondary"
                  onClick={() => window.location.reload()}
                >
                  <i className="fas fa-sync-alt me-2"></i>
                  Refresh
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Statistics Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="stat-card border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="stat-icon bg-primary text-white rounded-circle mx-auto mb-2">
                  <i className="fas fa-bell"></i>
                </div>
                <h4 className="text-primary mb-1">{notifications.length}</h4>
                <p className="text-muted mb-0">Total Notifications</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="stat-icon bg-danger text-white rounded-circle mx-auto mb-2">
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <h4 className="text-danger mb-1">{unreadCount}</h4>
                <p className="text-muted mb-0">Unread</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="stat-icon bg-warning text-white rounded-circle mx-auto mb-2">
                  <i className="fas fa-exclamation-circle"></i>
                </div>
                <h4 className="text-warning mb-1">
                  {getNotificationsByType('warning').length}
                </h4>
                <p className="text-muted mb-0">Warnings</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="stat-icon bg-info text-white rounded-circle mx-auto mb-2">
                  <i className="fas fa-info-circle"></i>
                </div>
                <h4 className="text-info mb-1">
                  {getNotificationsByType('info').length}
                </h4>
                <p className="text-muted mb-0">Info</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filter Tabs */}
        <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
          <Nav variant="tabs" className="mb-4">
            <Nav.Item>
              <Nav.Link eventKey="all">
                All Notifications
                <Badge bg="secondary" className="ms-2">
                  {notifications.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="unread">
                Unread
                {unreadCount > 0 && (
                  <Badge bg="danger" className="ms-2">
                    {unreadCount}
                  </Badge>
                )}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="products">
                Product Alerts
                <Badge bg="warning" className="ms-2">
                  {getNotificationsByType('warning').filter(n =>
                    n.title.toLowerCase().includes('product') ||
                    n.message.toLowerCase().includes('product')
                  ).length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="packing-materials">
                Packing Material Alerts
                <Badge bg="warning" className="ms-2">
                  {getNotificationsByType('warning').filter(n =>
                    n.title.toLowerCase().includes('packing material') ||
                    n.message.toLowerCase().includes('packing material')
                  ).length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="overdue">
                Overdue Orders
                <Badge bg="danger" className="ms-2">
                  {getNotificationsByType('danger').filter(n =>
                    n.title.toLowerCase().includes('overdue')
                  ).length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey={activeTab}>
              {filteredNotifications.length === 0 ? (
                <Card>
                  <Card.Body className="text-center py-5">
                    <i className="fas fa-bell-slash fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No notifications found</h5>
                    <p className="text-muted mb-0">
                      {activeTab === 'unread'
                        ? 'All caught up! No unread notifications.'
                        : `No ${activeTab.replace('-', ' ')} notifications at this time.`
                      }
                    </p>
                  </Card.Body>
                </Card>
              ) : (
                <Card>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th className="border-0 fw-semibold">Type</th>
                            <th className="border-0 fw-semibold">Title</th>
                            <th className="border-0 fw-semibold">Message</th>
                            <th className="border-0 fw-semibold">Status</th>
                            <th className="border-0 fw-semibold">Time</th>
                            <th className="border-0 fw-semibold">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredNotifications.map((notification) => (
                            <tr
                              key={notification.id}
                              className={!notification.read ? 'table-light' : ''}
                            >
                              <td className="border-0">
                                <div className="d-flex align-items-center">
                                  <i className={`${getNotificationIcon(notification.type)} me-2`}></i>
                                  {getNotificationBadge(notification.type)}
                                </div>
                              </td>
                              <td className="border-0">
                                <strong className="text-dark">
                                  {notification.title}
                                </strong>
                              </td>
                              <td className="border-0">
                                <div className="text-body-secondary">
                                  {notification.message}
                                </div>
                              </td>
                              <td className="border-0">
                                {!notification.read ? (
                                  <Badge bg="danger">Unread</Badge>
                                ) : (
                                  <Badge bg="secondary">Read</Badge>
                                )}
                              </td>
                              <td className="border-0">
                                <small className="text-muted">
                                  {new Date(notification.timestamp).toLocaleString()}
                                </small>
                              </td>
                              <td className="border-0">
                                <div className="d-flex gap-1">
                                  {!notification.read && (
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleMarkAsRead(notification.id)}
                                    >
                                      <i className="fas fa-check me-1"></i>
                                      Mark Read
                                    </Button>
                                  )}
                                  {notification.title.includes('Product') && (
                                    <Link
                                      to="/operations-dashboard?tab=stock"
                                      className="btn btn-outline-secondary btn-sm"
                                    >
                                      <i className="fas fa-eye me-1"></i>
                                      View Stock
                                    </Link>
                                  )}
                                  {notification.title.includes('Packing Material') && (
                                    <Link
                                      to="/operations-dashboard?tab=pmstock"
                                      className="btn btn-outline-secondary btn-sm"
                                    >
                                      <i className="fas fa-eye me-1"></i>
                                      View PM Stock
                                    </Link>
                                  )}
                                  {notification.title.includes('Overdue') && (
                                    <Link
                                      to="/dashboard?tab=orders"
                                      className="btn btn-outline-secondary btn-sm"
                                    >
                                      <i className="fas fa-eye me-1"></i>
                                      View Orders
                                    </Link>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Container>
    </Layout>
  );
};

export default Notifications;
